import axios from 'axios';
import { useContext, useState, useEffect } from 'react';

import { ModalContext } from 'app/app';
import DateHelper from 'helpers/dateUtils';
import { IconedButton } from 'components/IconedButton';
import InputUtils from 'helpers/inputUtils';
import {
  Animate,
  Grid,
  Search,
  Table,
  useNavigate,
  ViewContext,
} from 'components/lib';

import styles from './styles.module.scss';
import debounce from 'helpers/debounce';
import { ChevronLeft, ChevronRight } from 'feather-icons-react/build/IconComponents';
import { useSearchParams } from "react-router-dom";

export function Renewal() {
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);
  const dateFormatter = new DateHelper();
  const inputUtils = new InputUtils();
  const [payload, setPayload] = useState();
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQueryString, setSearchQueryString] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 1;
  const [page, setPage] = useState(initialPage);

  async function fetchRenewals(page, search, status, pageSize) {
    setLoading(true);
    setSearchParams({ page });
    await axios
      .get('/api/renewals/admin/search', {
        params: {
          page,
          search,
          status,
          pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      })
      .then((response) => {
        if (response.data && response.data.success) {
          setPayload(response.data);
          const formattedRenewals = response.data.data.map(
            (renewal) => ({
              id: renewal.id,
              Number: renewal.renewalNumber,
              'Agency name': renewal.agencyName ? (
                <a
                  href={
                    window.location.host === 'localhost:5002'
                      ? `http://localhost:5002/admin/agency/${renewal.agencyId}`
                      : `https://admin.coverbadger.com/admin/agency/${renewal.agencyId}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: 'blue',
                    textDecoration: 'underline',
                  }}
                >
                  {renewal.agencyName}
                </a>
              ) : (
                'N/A'
              ),
              'Company Name':
                renewal.companyName &&
                inputUtils
                  .capitalizeFirstLetter(renewal.companyName)
                  .replaceAll('Llc', 'LLC'),
              'Submission Number': renewal.submissionNumber ? (
                renewal.submissionNumber
              ) : (
                renewal.id
              ),
              'Created At':
                renewal.createdAt &&
                dateFormatter.timestamp(renewal.createdAt),
              'Renewal Date':
                renewal.renewalDate &&
                dateFormatter.formatIsoToCommomDate(renewal.renewalDate),
              status: renewal.status,
            })
          );
          setRenewals(formattedRenewals);
        } else {
          context.notification.show(
            'Unexpected API response structure',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      })
      .catch(() => {
        context.notification.show(
          'Failed to load renewals',
          'error',
          true,
          'toast',
          'x-circle'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchRenewals(
      page,
      searchQueryString,
      statusFilter,
      pageSize
    ).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQueryString, statusFilter, pageSize]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const debouncedSetSearchQueryString = debounce((query) => {
    setSearchQueryString(query);
    setPage(1);
  }, 500);

  return (
    <Animate type="pop">
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex' }}>
          {['Active', 'Bound', 'Declined', 'Expired'].map((status) => {
            return (
              <div
                key={status}
                style={{
                  marginLeft: '5px',
                  width: '150px',
                  backgroundColor:
                    statusFilter === status ? 'black' : '#EAEAEA',
                  color:
                    statusFilter === status ? 'white' : '#090E3480',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 10,
                  fontSize: 16,
                  fontWeight: 600,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  cursor: 'pointer',
                }}
                onClick={() => handleStatusFilter(status)}
              >
                {status}
              </div>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <IconedButton
            size="200px"
            color="#d32425"
            textColor="white"
            icon="plus-square"
            text="Create Renewal Offer"
            marginRight
            action={async () => {
              setModal({
                name: 'renewal_modal',
                action: () => {
                },
              });
            }}
          />
        </div>
      </div>
      <Grid cols="1">
        <div className={styles.wrapper} style={{ marginTop: 0 }}>
          <div
            style={{
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ width: '100%', marginTop: 30 }}>
              <Search
                callback={(query) => {
                  debouncedSetSearchQueryString(query);
                }}
              />
            </div>
          </div>

          <Table
            data={renewals}
            loading={loading}
            hide={[
              'id',
              'assignedUserEmail',
              'userId',
              'status',
            ]}
            actions={{
              custom: [
                {
                  icon: 'search',
                  color: '#637381',
                  action: (row) => {
                    navigate(
                      `/admin/renewal/${row.id}?page=${page}`
                    );
                  },
                },
              ],
            }}
          />
          {/* Pagination */}
          {payload && !loading && (
            <div className="flex items-center justify-between mb-2 mt-6 sm:mb-0">
              <div className="flex items-center">
                <span className="mr-2">Items per page</span>
                <select
                  className="
                  border bg-white border-gray-300
                  rounded-md shadow-sm p-2"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                >
                  {[10, 20, 30, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  className="
                    p-2 rounded-xl bg-gray-100
                    text-gray-500 hover:bg-gray-200
                    disabled:opacity-50"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft size={20} />
                </button>

                <span className="flex items-center">
                  <span className="mr-1">page</span>
                  <input
                    type="number"
                    min={1}
                    max={payload.pages}
                    defaultValue={page}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const newPage = e.target.value ? Number(e.target.value) : 1;
                        setPage(newPage);
                      }
                    }}
                    className="
                      w-16 p-2 rounded-xl border
                      border-gray-100 text-center"
                  />
                  <span className="ml-1">of {payload.pages}</span>
                </span>

                <button
                  className="
                    p-2 rounded-xl bg-gray-100
                    text-gray-500 hover:bg-gray-200
                    disabled:opacity-50"
                  onClick={() => setPage((prev) => Math.min(prev + 1, payload.pages))}
                  disabled={page === payload.pages}
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="flex items-center">
                <span>Total items: {payload.total}</span>
              </div>
            </div>
          )}
        </div>
      </Grid>
    </Animate>
  );
}